<template>
  <div class="position-relative overflow-auto">
    <!-- TABLE WRAPPER -->
    <div class="position-relative w-100">
      <table class="table table-bordered text-center align-middle">
        <thead>
          <tr>
            <th
              v-for="(col, i) in internal.columns"
              :key="'col-' + i"
              :class="[
                'non-editable-row',
                isColumnHighlighted(i) ? 'col-highlight' : '',
              ]"
              :style="getColumnStyle(i)"
            >
              <div v-if="editable" class="d-flex align-items-start gap-2">
                <div class="flex-grow-1">
                  <!-- Column name -->
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    :class="isColumnHighlighted(i) ? 'highlight-input' : ''"
                    :value="internal.columns[i]"
                    @input="onColumnNameInput(i, $event.target.value)"
                  />

                  <!-- Column width (use what user typed) -->
                  <div class="d-flex align-items-center mt-2">
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      step="1"
                      class="form-control form-control-sm width-input"
                      :class="isColumnHighlighted(i) ? 'highlight-input' : ''"
                      :value="internal.columnWidths[i]"
                      @input="onColumnWidthInput(i, $event.target.value)"
                      title="Šířka sloupce v %"
                    />
                    <span class="ms-2 small text-nowrap">%</span>
                  </div>
                </div>

                <div class="d-flex flex-column gap-2">
                  <button
                    type="button"
                    class="btn btn-sm btn-blue-main"
                    @click.stop="toggleColumnHighlight(i)"
                    title="Zvýraznit sloupec"
                  >
                    ★
                  </button>

                  <button
                    class="btn btn-sm btn-red"
                    @click="openDeleteColumnModal(i)"
                    title="Smazat sloupec"
                  >
                    &times;
                  </button>
                </div>
              </div>

              <div v-else>
                {{ col }}
              </div>
            </th>

            <th v-if="editable" class="non-editable-row">Akce</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(row, rowIndex) in internal.rows" :key="'row-' + rowIndex">
            <td
              v-for="(col, colIndex) in internal.columns"
              :key="rowIndex + '-' + colIndex"
              :class="[
                getCellClasses(rowIndex, colIndex),
                isBold(rowIndex, colIndex) ? 'bold-cell' : '',
              ]"
              :style="getColumnStyle(colIndex)"
            >
              <input
                v-if="editable"
                type="text"
                class="form-control"
                :class="[
                  isCellHighlighted(rowIndex, colIndex)
                    ? 'highlight-input'
                    : '',
                  isBold(rowIndex, colIndex) ? 'bold-input' : '',
                ]"
                :value="internal.rows[rowIndex][col]"
                @input="onCellInput(rowIndex, col, $event.target.value)"
                @keydown.ctrl.b.prevent="toggleBold(rowIndex, colIndex)"
              />

              <span v-else>{{ row[col] }}</span>
            </td>

            <td v-if="editable" class="cell-actions">
              <button
                type="button"
                class="btn btn-blue-main btn-sm me-2"
                @click="toggleRowHighlight(rowIndex)"
                title="Zvýraznit řádek"
              >
                ★
              </button>

              <button
                class="btn btn-red btn-sm"
                @click="openDeleteRowModal(rowIndex)"
              >
                Smazat
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <button
        v-if="editable"
        class="btn btn-blue-main rounded-circle shadow position-absolute"
        style="
          right: -45px;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
        "
        @click="addColumn"
      >
        +
      </button>
    </div>

    <div class="d-flex justify-content-center">
      <button
        v-if="editable"
        class="btn btn-blue-main rounded-circle shadow"
        style="width: 45px; height: 45px"
        @click="addRow"
      >
        +
      </button>
    </div>

    <!-- DELETE COLUMN MODAL -->
    <div class="modal fade" id="deleteColumnModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title">Delete Column</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            Are you sure you want to delete column:
            <strong>{{ internal.columns[pendingDeleteColumn] }}</strong
            >?
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" data-bs-dismiss="modal">
              Cancel
            </button>
            <button class="btn btn-red" @click="confirmDeleteColumn">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- DELETE ROW MODAL -->
    <div class="modal fade" id="deleteRowModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title">Delete Row</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            Are you sure you want to delete this row?
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" data-bs-dismiss="modal">
              Cancel
            </button>
            <button class="btn btn-red" @click="confirmDeleteRow">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";

export default {
  name: "EditableTable",

  props: {
    modelValue: {
      type: Object,
      default: () => ({
        columns: ["Column 1"],
        rows: [{ "Column 1": "" }],
        highlightedRows: [],
        highlightedColumns: [],
        boldMap: {},
        columnWidths: [100], // NEW
      }),
    },
    editable: { type: Boolean, default: true },
  },

  data() {
    return {
      internal: {
        columns: [],
        rows: [],
        boldMap: {},
        columnWidths: [], // NEW
      },

      pendingDeleteColumn: null,
      pendingDeleteRow: null,

      modalDeleteColumn: null,
      modalDeleteRow: null,

      highlightedRows: [],
      highlightedColumns: [],
    };
  },

  mounted() {
    this.loadInternalData();

    this.modalDeleteColumn = new Modal(
      document.getElementById("deleteColumnModal")
    );
    this.modalDeleteRow = new Modal(document.getElementById("deleteRowModal"));
  },

  watch: {
    modelValue: {
      deep: true,
      handler() {
        this.loadInternalData();
      },
    },
  },

  methods: {
    loadInternalData() {
      this.internal.columns = [...(this.modelValue.columns || [])];
      this.internal.rows = JSON.parse(
        JSON.stringify(this.modelValue.rows || [])
      );

      // COLUMN WIDTHS (use what user typed; just keep array in sync)
      const incomingWidths = Array.isArray(this.modelValue.columnWidths)
        ? [...this.modelValue.columnWidths]
        : [];

      if (incomingWidths.length === this.internal.columns.length) {
        this.internal.columnWidths = incomingWidths.map((n) =>
          this.toValidWidth(n)
        );
      } else {
        // fallback: if missing/mismatch, set default widths
        this.internal.columnWidths = this.internal.columns.map(() => 20);
      }

      // BOLD MAP
      if (!this.modelValue.boldMap) {
        this.internal.boldMap = {};
        this.internal.rows.forEach((_, r) => {
          this.internal.boldMap[r] = {};
          this.internal.columns.forEach((_, c) => {
            this.internal.boldMap[r][c] = false;
          });
        });
      } else {
        this.internal.boldMap = JSON.parse(
          JSON.stringify(this.modelValue.boldMap)
        );
      }

      this.highlightedRows = [...(this.modelValue.highlightedRows || [])];
      this.highlightedColumns = [...(this.modelValue.highlightedColumns || [])];
    },

    // clamp only (does NOT normalize)
    toValidWidth(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return 20;
      return Math.min(1000, Math.max(1, Math.round(n)));
    },

    getColumnStyle(colIndex) {
      const w = this.internal.columnWidths?.[colIndex];
      if (!w) return {};
      return { width: `${w}%` };
    },

    onColumnWidthInput(index, newValue) {
      const v = this.toValidWidth(newValue);
      this.internal.columnWidths.splice(index, 1, v);
      this.emitSave();
    },

    // BOLD TOGGLE (CTRL + B)
    toggleBold(row, col) {
      this.internal.boldMap[row][col] = !this.internal.boldMap[row][col];
      this.emitSave();
    },

    isBold(row, col) {
      return this.internal.boldMap?.[row]?.[col] === true;
    },

    onCellInput(row, colName, value) {
      this.internal.rows[row][colName] = value;
      this.emitSave();
    },

    addRow() {
      const newRow = {};
      this.internal.columns.forEach((c) => (newRow[c] = ""));
      this.internal.rows.push(newRow);

      const rowIndex = this.internal.rows.length - 1;
      this.internal.boldMap[rowIndex] = {};
      this.internal.columns.forEach((_, c) => {
        this.internal.boldMap[rowIndex][c] = false;
      });

      this.emitSave();
    },

    addColumn() {
      const name = `Column ${this.internal.columns.length + 1}`;
      this.internal.columns.push(name);

      this.internal.rows.forEach((r, rowIndex) => {
        r[name] = "";
        this.internal.boldMap[rowIndex][
          this.internal.columns.length - 1
        ] = false;
      });

      // width for new column: default value, do not touch others
      this.internal.columnWidths.push(20);

      this.emitSave();
    },

    openDeleteRowModal(index) {
      this.pendingDeleteRow = index;
      this.modalDeleteRow.show();
    },

    confirmDeleteRow() {
      const idx = this.pendingDeleteRow;
      this.internal.rows.splice(idx, 1);

      delete this.internal.boldMap[idx];
      this.internal.boldMap = Object.values(this.internal.boldMap); // reindex

      this.modalDeleteRow.hide();
      this.emitSave();
    },

    openDeleteColumnModal(index) {
      this.pendingDeleteColumn = index;
      this.modalDeleteColumn.show();
    },

    confirmDeleteColumn() {
      const colIndex = this.pendingDeleteColumn;
      const colName = this.internal.columns[colIndex];

      this.internal.columns.splice(colIndex, 1);
      this.internal.rows.forEach((r) => delete r[colName]);

      this.internal.rows.forEach((_, r) => {
        delete this.internal.boldMap[r][colIndex];
        this.internal.boldMap[r] = Object.values(this.internal.boldMap[r]);
      });

      // remove its width (do not normalize others)
      this.internal.columnWidths.splice(colIndex, 1);

      this.modalDeleteColumn.hide();
      this.emitSave();
    },

    onColumnNameInput(index, newName) {
      const oldName = this.internal.columns[index];
      if (oldName === newName) return;

      this.internal.rows.forEach((row) => {
        row[newName] = row[oldName];
        delete row[oldName];
      });

      this.internal.columns.splice(index, 1, newName);
      this.emitSave();
    },

    isRowHighlighted(i) {
      return this.highlightedRows.includes(i);
    },

    isColumnHighlighted(i) {
      return this.highlightedColumns.includes(i);
    },

    getCellClasses(row, col) {
      return {
        "row-highlight": this.isRowHighlighted(row),
        "col-highlight": this.isColumnHighlighted(col),
      };
    },

    isCellHighlighted(row, col) {
      return this.isRowHighlighted(row) || this.isColumnHighlighted(col);
    },

    toggleRowHighlight(i) {
      if (this.highlightedRows.includes(i)) {
        this.highlightedRows = this.highlightedRows.filter((x) => x !== i);
      } else {
        this.highlightedRows.push(i);
      }
      this.emitSave();
    },

    toggleColumnHighlight(i) {
      if (this.highlightedColumns.includes(i)) {
        this.highlightedColumns = this.highlightedColumns.filter(
          (x) => x !== i
        );
      } else {
        this.highlightedColumns.push(i);
      }
      this.emitSave();
    },

    emitSave() {
      this.$emit("update:modelValue", {
        columns: [...this.internal.columns],
        rows: JSON.parse(JSON.stringify(this.internal.rows)),
        highlightedRows: [...this.highlightedRows],
        highlightedColumns: [...this.highlightedColumns],
        boldMap: JSON.parse(JSON.stringify(this.internal.boldMap)),
        columnWidths: [...this.internal.columnWidths],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  width: 100%;
  min-width: 600px;
  table-layout: fixed; /* makes % widths stick */
}

.table {
  margin: 0 !important;
}

tr {
  background: $white;
}

.non-editable-row {
  background-color: $blue-main;
  color: $white;
}

th {
  padding: 1.4rem;
  font-size: 1.4rem;
}

th.col-highlight {
  color: $white;
}

td.row-highlight,
td.col-highlight {
  background-color: rgba($blue-main, 0.75) !important;
  color: $white;
}

.highlight-input {
  background-color: rgba($blue-main, 0.75) !important;
  color: $white;
  border-color: rgba($white, 0.7);
}

.bold-cell {
  font-weight: bold !important;
}

.bold-input {
  font-weight: bold !important;
}

.cell-actions {
  background: $white;
}

.width-input {
  max-width: 90px;
}
</style>
